import { makeObservable, observable, action, computed, runInAction, toJS } from "mobx";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { api } from "@/modules/api";
import { uuidModule } from "@/modules/uuid";
import { toastModule } from "@/modules/toast";
import { logger } from "@/modules/logger";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { resolveCapturedAudioSessionSyncModelUuid } from "@/modules/uuid/sync-models/resolveCapturedAudioSessionSyncModelUuid";
import { Uuid } from "@/domains/global/identifiers";
import axios from "axios";
import { NoteObservable } from "@/store/note/NoteObservable";
import audioProcessor from "./audio-processor.js?raw";
import { debounce } from "lodash-es";
import { AssemblyAIMessage, TranscriptSnapshot } from "@/domains/db/audio-capture/types";
import { transformNoteWithVoice } from "@/actions/transformNoteWithVoice";

export enum RecordingStatus {
  IDLE = "IDLE",
  LOCKED = "LOCKED",
  INITIALIZING = "INITIALIZING",
  INITIALIZED = "INITIALIZED",
  RECORDING = "RECORDING",
  PAUSED = "PAUSED",
  STOPPED = "STOPPED",
  UPLOADING = "UPLOADING",
  UPLOADED = "UPLOADED",
  PROCESSING = "PROCESSING",
}

interface AudioBufferItem {
  data: ArrayBuffer;
  timestamp: number;
}

export interface AudioCaptureStore {}

export class AppStoreAudioCaptureStore extends AppSubStore implements AudioCaptureStore {
  readonly DURATION_UPDATE_INTERVAL_MS = 1000; // 1 second
  readonly SAMPLE_RATE = 16000; // AssemblyAI expects 16kHz
  readonly TOKEN_EXPIRATION_BUFFER_MS = 30 * 1000; // 30 seconds
  readonly TOKEN_CHECK_INTERVAL_MS = 10 * 1000; // 10 seconds
  readonly WEBSOCKET_CONNECTION_TIMEOUT_MS = 5000; // 5 seconds
  readonly POST_STOP_BUFFER_MS = 1000; // 1 second
  readonly ASSEMBLY_AI_WS_URL = "wss://api.assemblyai.com/v2/realtime/ws";

  // Buffering and reconnection constants
  readonly MAX_AUDIO_BUFFER_SIZE = 1000; // Maximum number of audio chunks to buffer
  readonly AUDIO_BUFFER_FLUSH_INTERVAL_MS = 50; // Flush buffered audio every 50ms
  readonly RECONNECTION_DELAY_MS = 1000; // 1 second delay between reconnection attempts
  readonly MAX_RECONNECTION_ATTEMPTS = Infinity; // Maximum number of reconnection attempts

  // Global state for audio capture
  activeNoteId: string | null = null;
  capturedAudioId: string = uuidModule.generate();

  // Shared audio stream for both recording and streaming
  stream: MediaStream | null = null;
  status: RecordingStatus = RecordingStatus.IDLE;
  error: string | null = null;

  // Audio metadata
  durationInterval: NodeJS.Timeout | null = null;
  duration: number = 0;
  startTime: number = 0;
  pausedTime: number = 0;
  totalPausedTime: number = 0;

  // Audio restoration
  restoredDuration: number = 0;

  // Audio recording
  mediaRecorder: MediaRecorder | null = null;
  audioChunks: Blob[] = [];
  audioBlob: Blob | null = null;
  audioUrl: string | null = null;
  releaseRecordingLock: ((value: unknown) => void) | null = null;

  // Audio streaming + transcription
  wsStatusHeartbeat = Date.now();
  wsConnection: WebSocket | null = null;
  audioContext: AudioContext | null = null;
  audioWorkletNode: AudioWorkletNode | null = null;
  audioSource: MediaStreamAudioSourceNode | null = null;
  transcriptMap: Map<number, AssemblyAIMessage> = new Map();
  transcriptSnapshots: TranscriptSnapshot[] = [];
  isRecordingWithoutTranscription: boolean = false;

  // Token Management
  assemblyAiToken: string | null = null;
  assemblyAiTokenExpiresAt: number | null = null;
  tokenCheckInterval: NodeJS.Timeout | null = null;
  isRefreshingToken: boolean = false;

  // WebSocket resilience and buffering
  audioBuffer: AudioBufferItem[] = [];
  audioBufferFlushInterval: NodeJS.Timeout | null = null;
  isReconnecting: boolean = false;
  reconnectionAttempts: number = 0;
  reconnectionTimeout: NodeJS.Timeout | null = null;
  isWebSocketHealthy: boolean = false;
  lastWebSocketActivity: number = 0;
  webSocketHealthCheckInterval: NodeJS.Timeout | null = null;

  // Audio Visualization
  analyser: AnalyserNode | null = null;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);
    makeObservable(this, {
      POST_STOP_BUFFER_MS: true,
      // Constants
      DURATION_UPDATE_INTERVAL_MS: false,
      SAMPLE_RATE: false,
      ASSEMBLY_AI_WS_URL: false,
      TOKEN_CHECK_INTERVAL_MS: false,
      TOKEN_EXPIRATION_BUFFER_MS: false,
      MAX_AUDIO_BUFFER_SIZE: false,
      AUDIO_BUFFER_FLUSH_INTERVAL_MS: false,
      RECONNECTION_DELAY_MS: false,
      MAX_RECONNECTION_ATTEMPTS: false,
      WEBSOCKET_CONNECTION_TIMEOUT_MS: false,

      // Global Audio Capture State
      activeNoteId: observable,
      activeNoteObservable: computed,
      capturedAudioId: observable,
      sessionId: computed,
      supportedMimeType: computed,

      // Shared audio stream for both recording and streaming
      stream: observable,
      status: observable,
      isNoteRecording: computed,
      hasFinishedAudioRecording: computed,
      isStartRecordingDisabled: computed,
      isIndicatorVisible: computed,
      isRecordingWithoutTranscription: observable,
      error: observable,

      // Audio metadata
      durationInterval: observable,
      tokenCheckInterval: observable,
      duration: observable,
      startTime: observable,
      pausedTime: observable,
      totalPausedTime: observable,
      formattedDuration: computed,
      _getAudioDuration: false,
      _getFileExtensionFromMimeType: false,

      // Audio restoration
      restoredDuration: observable,

      // Audio Recording
      mediaRecorder: observable,
      audioChunks: observable,
      audioBlob: observable,
      audioUrl: observable,
      releaseRecordingLock: false,

      // Audio Visualization
      analyser: observable,

      // HELPERS (Recording)
      _canStartRecording: false,
      _uploadRecording: action,
      _transformAudioToNote: action,
      _withAudioCaptureLock: action,
      _updateDuration: action,
      _mediaRecorderOnDataAvailable: false,
      _initializeRecording: action,
      _initializeRecordingWithoutTranscription: action,
      _startRecording: action,

      // Audio Streaming + Transcription
      audioContext: observable,
      audioWorkletNode: observable,
      audioSource: observable,
      transcriptMap: observable,
      transcript: computed,
      wsStatusHeartbeat: observable,
      wsConnection: observable,
      connectionStatus: computed,
      isTranscriptionActive: computed,
      isTranscriptionPaused: computed,

      // Token Management
      assemblyAiToken: observable,
      assemblyAiTokenExpiresAt: observable,
      isRefreshingToken: observable,

      // WebSocket resilience and buffering
      audioBuffer: observable,
      audioBufferFlushInterval: observable,
      isReconnecting: observable,
      reconnectionAttempts: observable,
      reconnectionTimeout: observable,
      transcriptSnapshots: observable,
      isWebSocketHealthy: observable,
      lastWebSocketActivity: observable,
      webSocketHealthCheckInterval: observable,

      // HELPERS (Websockets + Transcription)
      _createWebSocketConnection: action,
      _initializeWebSocket: action,
      _handleWebSocketMessage: action,
      _sendAudioToWebSocket: action,
      _cleanupWebSocketAndAudio: action,

      // HELPERS (Token Management)
      _getAssemblyAiToken: action,
      _refreshTokenAndReconnectWebSocket: action,
      _startTokenMonitoring: action,
      _stopTokenMonitoring: action,
      _isTokenExpiredOrExpiringSoon: false,

      // HELPERS (Buffering and Reconnection)
      _bufferAudioData: action,
      _flushAudioBuffer: action,
      _startAudioBufferFlush: action,
      _stopAudioBufferFlush: action,
      _handleWebSocketDisconnection: action,
      _attemptWebSocketReconnection: false,
      _attemptWebSocketReconnectionAfterDelay: false,
      _startWebSocketHealthCheck: action,
      _stopWebSocketHealthCheck: action,
      _checkWebSocketHealth: action,
      _createTranscriptSnapshot: action,

      // External Actions
      initializeFromDb: action,
      initializeAndStartRecording: action,
      pauseRecording: action,
      resumeRecording: action,
      stopRecording: action,
      processRecording: action,
      resetRecording: action,
      clearRecording: action,
      clearAudioCapturesByNoteId: action,
    });

    setInterval(() => {
      runInAction(() => (this.wsStatusHeartbeat = Date.now()));
    }, 2_000);

    window.addEventListener("beforeunload", event => {
      if (this.status === RecordingStatus.RECORDING) {
        event.preventDefault();
        event.stopPropagation();
        return "Audio is being captured. Are you sure you want to leave?";
      }
    });
  }

  get activeNoteObservable(): NoteObservable | undefined {
    if (!this.activeNoteId) return undefined;
    return this.store.notes.get(this.activeNoteId);
  }

  get sessionId(): Uuid {
    return resolveCapturedAudioSessionSyncModelUuid({
      capturedAudioId: this.capturedAudioId,
    });
  }

  get formattedDuration(): string {
    const totalDuration = this.duration + this.restoredDuration;
    const minutes = Math.floor(totalDuration / 60);
    const seconds = Math.round(totalDuration % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  get supportedMimeType(): string | undefined {
    const preferred = "audio/webm";
    const fallback = "audio/mp4";
    const mime = MediaRecorder.isTypeSupported(preferred)
      ? preferred
      : MediaRecorder.isTypeSupported(fallback)
        ? fallback
        : undefined;
    return mime;
  }

  get isNoteRecording(): boolean {
    return this.status === RecordingStatus.RECORDING && this.activeNoteId !== null;
  }

  get hasFinishedAudioRecording() {
    return [
      RecordingStatus.PROCESSING,
      RecordingStatus.UPLOADING,
      RecordingStatus.UPLOADED,
    ].includes(this.status);
  }

  get isStartRecordingDisabled() {
    return this.status !== RecordingStatus.IDLE && this.activeNoteObservable?.isDeleted !== true;
  }

  get isIndicatorVisible() {
    return this.isStartRecordingDisabled && !this.hasFinishedAudioRecording;
  }

  get transcript(): string {
    const allTranscripts: string[] = [];

    // Add all previous snapshots in order
    this.transcriptSnapshots.forEach(snapshot => {
      const sortedMessages = Array.from(snapshot.transcriptMap.values()).sort(
        (a, b) => a.audio_start - b.audio_start
      );
      const snapshotText = sortedMessages
        .map(message => message.text)
        .join(" ")
        .trim();
      if (snapshotText) allTranscripts.push(snapshotText);
    });

    // Add current transcript
    if (this.transcriptMap.size > 0) {
      const sortedMessages = Array.from(this.transcriptMap.values()).sort(
        (a, b) => a.audio_start - b.audio_start
      );
      const currentText = sortedMessages
        .map(message => message.text)
        .join(" ")
        .trim();
      if (currentText) allTranscripts.push(currentText);
    }

    let result = allTranscripts.join(" ...[temporarily disconnected]... ").trim();

    if (this.reconnectionAttempts >= this.MAX_RECONNECTION_ATTEMPTS && !this.isReconnecting) {
      result += " ...[transcription ended]";
    }

    return result;
  }

  get connectionStatus(): { time: number; status: string } {
    if (!this.wsConnection) return { time: this.wsStatusHeartbeat, status: "disconnected" };

    switch (this.wsConnection.readyState) {
      case WebSocket.CONNECTING:
        return { time: this.wsStatusHeartbeat, status: "connecting" };
      case WebSocket.OPEN:
        return {
          time: this.wsStatusHeartbeat,
          status: this.isWebSocketHealthy ? "connected" : "unhealthy",
        };
      case WebSocket.CLOSING:
        return { time: this.wsStatusHeartbeat, status: "disconnecting" };
      case WebSocket.CLOSED:
        return { time: this.wsStatusHeartbeat, status: "disconnected" };
      default:
        return { time: this.wsStatusHeartbeat, status: "unknown" };
    }
  }

  get isTranscriptionActive(): boolean {
    return (
      this.status === RecordingStatus.RECORDING &&
      this.connectionStatus.status === "connected" &&
      this.isWebSocketHealthy
    );
  }

  get isTranscriptionPaused(): boolean {
    return (
      this.status === RecordingStatus.RECORDING && this.connectionStatus.status !== "connected"
    );
  }

  /**
   * HELPER FUNCTIONS
   */

  _canStartRecording(noteId: string): boolean {
    if (this.status === RecordingStatus.IDLE) return true;
    if (this.status === RecordingStatus.LOCKED && this.activeNoteId === noteId) return true;
    return false;
  }

  _withAudioCaptureLock = async (
    f: () => Promise<void>,
    { showErrorToast }: { showErrorToast?: boolean } = { showErrorToast: true }
  ) => {
    if (this.status === RecordingStatus.LOCKED) return f();

    const failedToGetLockMessage = "Failed to get lock to start recording";
    // Lock audio capture so there's a single recording session at a time across tabs.
    try {
      await navigator.locks.request(
        "audio-capture-recording",
        {
          mode: "exclusive",
          ifAvailable: true,
        },
        async lock => {
          if (!lock) throw new Error(failedToGetLockMessage);

          logger.debug({
            message: "[AppStoreAudioCaptureStore] got lock to start recording",
          });

          await f();

          return new Promise(resolve => {
            this.releaseRecordingLock = resolve;
          });
        }
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error({
        message: "[AppStoreAudioCaptureStore] Failed",
        info: {
          error: errorMessage,
        },
      });

      if (showErrorToast) {
        const isFailedToGetLock = errorMessage.includes(failedToGetLockMessage);
        toastModule.triggerToast({
          content: isFailedToGetLock
            ? "Cannot start recording while Mem is recording on another tab."
            : "Failed to start recording, please try again.",
          toastId: this.capturedAudioId,
        });
      }
    }
  };

  _updateDuration() {
    const currentTime = Date.now();
    const elapsedTime = currentTime - this.startTime - this.totalPausedTime;
    this.duration = Math.floor(elapsedTime / 1000);
  }

  async _getAudioDuration(audioBlob: Blob): Promise<number> {
    return new Promise((resolve, reject) => {
      const audioContext = new AudioContext();
      const reader = new FileReader();

      reader.onload = async () => {
        try {
          const arrayBuffer = reader.result as ArrayBuffer;
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          resolve(audioBuffer.duration);
        } catch (error) {
          reject(error);
        } finally {
          audioContext.close();
        }
      };

      reader.onerror = () => {
        reject(new Error("Failed to read audio file"));
        audioContext.close();
      };

      reader.readAsArrayBuffer(audioBlob);
    });
  }

  _getFileExtensionFromMimeType(mimeType: string): string {
    const mimeToExtension: Record<string, string> = {
      "audio/webm": "webm",
      "audio/mp4": "mp4",
      "audio/wav": "wav",
      "audio/ogg": "ogg",
      "audio/mpeg": "mp3",
      "audio/aac": "aac",
    };

    return mimeToExtension[mimeType] || "webm"; // fallback to webm
  }

  _mediaRecorderOnDataAvailable = (ev: BlobEvent) => {
    if (ev.data.size === 0 || !this.activeNoteId) return;

    this.audioChunks.push(ev.data);
    this.store.memDb.audioCaptures.appendAudioBlob(this.activeNoteId, ev.data);
  };

  async _uploadRecording(): Promise<{ success: true } | { success: false; error: string }> {
    logger.debug({ message: "[AppStoreAudioCaptureStore] uploadRecording" });

    if (!this.audioBlob) {
      return {
        success: false,
        error: "No audio recording available to upload",
      };
    }

    const mimeType = this.audioBlob.type || this.supportedMimeType;
    if (!mimeType) {
      return {
        success: false,
        error: "No supported MIME type found",
      };
    }

    const fileExtension = this._getFileExtensionFromMimeType(mimeType);

    // Try direct upload first
    try {
      if (!this.audioBlob) {
        throw new Error("No audio recording available to upload");
      }

      const mimeType = this.audioBlob.type || this.supportedMimeType;
      if (!mimeType) throw new Error("No supported MIME type found");

      const fileExtension = this._getFileExtensionFromMimeType(mimeType);

      const response = await api.post("/v2/voice/generate_signed_url", {
        body: {
          filename: `${this.capturedAudioId}.${fileExtension}`,
          mime_type: mimeType,
          captured_audio_id: this.capturedAudioId,
          transcript_text: this.transcript,
          transcript_provider: "ASSEMBLY_AI",
        },
      });

      logger.debug({
        message: "[AppStoreAudioCaptureStore][_uploadRecording] Get Signed URL Response",
        info: { upload_url: response.data?.upload_url || null, mimeType },
      });

      if (!response.data?.upload_url) throw new Error("Failed to get upload URL");

      const { upload_url } = response.data;

      const uploadResponse = await axios.put(upload_url, this.audioBlob, {
        headers: { "Content-Type": mimeType },
      });

      logger.debug({
        message: "[AppStoreAudioCaptureStore][_uploadRecording] Upload Response",
        info: { status: uploadResponse.status },
      });

      if (uploadResponse.status !== 200) {
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
      }

      return { success: true };
    } catch (directUploadError: unknown) {
      const directUploadErrorMessage =
        directUploadError instanceof Error ? directUploadError.message : "Direct upload failed";

      logger.warn({
        message:
          "[AppStoreAudioCaptureStore][_uploadRecording] Direct upload failed, trying fallback",
        info: { error: directUploadErrorMessage },
      });

      // Fallback to direct endpoint upload
      try {
        const formData = new FormData();
        formData.append("audio_file", this.audioBlob, `${this.capturedAudioId}.${fileExtension}`);
        formData.append("captured_audio_id", this.capturedAudioId);
        formData.append("filename", `${this.capturedAudioId}.${fileExtension}`);
        formData.append("mime_type", mimeType);
        formData.append("transcript_text", this.transcript);
        formData.append("transcript_provider", "ASSEMBLY_AI");

        // Use bodySerializer to properly handle FormData with openapi-fetch
        // https://openapi-ts.dev/openapi-fetch/api#bodyserializer
        const fallbackResponse = await api.post("/v2/voice/upload", {
          body: {
            // @ts-expect-error - audio_file is a Blob
            audio_file: this.audioBlob,
            captured_audio_id: this.capturedAudioId,
            filename: `${this.capturedAudioId}.${fileExtension}`,
            mime_type: mimeType,
            transcript_text: this.transcript,
            transcript_provider: "ASSEMBLY_AI",
          },
          bodySerializer(body) {
            const fd = new FormData();
            fd.append("audio_file", body.audio_file as unknown as Blob, body.filename);
            fd.append("captured_audio_id", body.captured_audio_id);
            fd.append("filename", body.filename);
            fd.append("mime_type", body.mime_type);
            if (body.transcript_text) {
              fd.append("transcript_text", body.transcript_text);
            }
            if (body.transcript_provider) {
              fd.append("transcript_provider", body.transcript_provider);
            }
            return fd;
          },
        });

        logger.debug({
          message: "[AppStoreAudioCaptureStore][_uploadRecording] Fallback upload success",
          info: { hasError: !!fallbackResponse.error },
        });

        if (fallbackResponse.error) {
          throw new Error(`Upload failed: ${fallbackResponse.error.detail}`);
        }

        return { success: true };
      } catch (fallbackError: unknown) {
        const fallbackErrorMessage =
          fallbackError instanceof Error ? fallbackError.message : "Fallback upload failed";

        logger.error({
          message: "[AppStoreAudioCaptureStore][_uploadRecording] Both upload methods failed",
          info: {
            directUploadError: directUploadErrorMessage,
            fallbackError: fallbackErrorMessage,
          },
        });

        return { success: false, error: `File upload failed. Please try again.` };
      }
    }
  }

  async _transformAudioToNote(): Promise<{ success: boolean; error?: string }> {
    logger.debug({ message: "[AppStoreAudioCaptureStore] transformAudioToNote" });
    try {
      if (!this.activeNoteId) return { success: false, error: "No note ID" };
      const { success, error } = await transformNoteWithVoice({
        noteId: this.activeNoteId,
        capturedAudioId: this.capturedAudioId,
        sessionId: this.sessionId,
        store: this.store,
      });
      return { success, error };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to transform audio to note";
      logger.error({
        message: "[AppStoreAudioCaptureStore][_transformAudioToNote] Transform Error",
        info: { error: errorMessage },
      });
      return { success: false, error: errorMessage };
    }
  }

  /**
   * STATE TRANSITIONS
   */

  // IDLE -> INITIALIZED
  async _initializeRecording(noteId: string): Promise<void> {
    if (!this._canStartRecording(noteId)) {
      toastModule.triggerToast({
        content: "Cannot initialize recording when the recorder is not idle",
        toastId: this.capturedAudioId,
      });
      logger.error({
        message: "[AppStoreAudioCaptureStore] initializeRecording",
        info: { error: "Cannot initialize recording when the recorder is not idle" },
      });
      return;
    }

    if (!this.supportedMimeType) {
      toastModule.triggerToast({
        content: "Browser does not support audio recording. Please use a different browser.",
        toastId: this.capturedAudioId,
      });
      logger.error({
        message: "[AppStoreAudioCaptureStore] initializeRecording",
        info: { error: "Browser does not support audio recording" },
      });
      return;
    }

    runInAction(() => {
      if (this.status !== RecordingStatus.LOCKED) {
        // Ensure a clean state when not locked.
        this.store.memDb.audioCaptures.clearAudioCapturesByNoteId(noteId);

        this.transcriptMap.clear();
        this.audioChunks = [];
        this.transcriptSnapshots = [];
        this.capturedAudioId = uuidModule.generate();
      }
      this.status = RecordingStatus.INITIALIZING;
      this.activeNoteId = noteId;
      this.audioBuffer = [];
      this.reconnectionAttempts = 0;
      this.isReconnecting = false;
    });

    try {
      // Create a stream to be used for both the mediaRecorder and the audioSource
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { sampleRate: this.SAMPLE_RATE },
      });

      // Initialize MediaRecorder - This is audio -> file
      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType: this.supportedMimeType });
      this.mediaRecorder.ondataavailable = this._mediaRecorderOnDataAvailable;

      // Reference set up from Assembly AI: https://github.com/AssemblyAI/realtime-transcription-browser-js-example/blob/master/public/index.js
      // Initialize AudioWorkletNode - This is for audio -> stream -> transcription
      this.audioContext = new AudioContext({
        sampleRate: this.SAMPLE_RATE,
        latencyHint: "balanced",
      });

      const analyser = this.audioContext.createAnalyser();
      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.8;

      runInAction(() => {
        this.analyser = analyser;
      });

      this.audioSource = this.audioContext.createMediaStreamSource(this.stream);
      this.audioSource.connect(analyser);

      // Original: https://stackoverflow.com/a/52774181
      const processorBlob = new Blob([audioProcessor], { type: "text/javascript" });
      const processorURL = URL.createObjectURL(processorBlob);
      await this.audioContext.audioWorklet.addModule(processorURL);

      this.audioWorkletNode = new AudioWorkletNode(this.audioContext, "audio-processor");
      this.audioSource.connect(this.audioWorkletNode);
      this.audioWorkletNode.connect(this.audioContext.destination);
      this.audioWorkletNode.port.onmessage = event => {
        this._sendAudioToWebSocket(event.data.audio_data);
      };

      runInAction(() => {
        this.startTime = Date.now();
        this.totalPausedTime = 0;
        this.pausedTime = 0;
        this.status = RecordingStatus.INITIALIZED;
        this.error = null;
      });

      // Initialize WebSocket in the background - this allows audio to be buffered while waiting
      this._initializeWebSocket().catch(error => {
        logger.error({
          message: "[AppStoreAudioCaptureStore] Background WebSocket initialization failed",
          info: { error: error instanceof Error ? error.message : String(error) },
        });
      });
    } catch (error) {
      // If the browser doesn't support the sample rate, switch to recording-only
      if (error instanceof DOMException && error.name === "NotSupportedError") {
        await this._cleanupWebSocketAndAudio();
        runInAction(() => this.resetRecording({ preserveLockedStatus: true }));
        await this._initializeRecordingWithoutTranscription(noteId);
        return;
      }

      toastModule.triggerToast({
        content: "Failed to initialize recording",
        toastId: this.capturedAudioId,
      });
      logger.error({
        message: "[AppStoreAudioCaptureStore] initializeRecording",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
      runInAction(() => this.resetRecording({ preserveLockedStatus: true }));
    }
  }

  async _initializeRecordingWithoutTranscription(noteId: string): Promise<void> {
    if (!this._canStartRecording(noteId)) {
      toastModule.triggerToast({
        content: "Cannot initialize recording when the recorder is not idle",
        toastId: this.capturedAudioId,
      });
      logger.error({
        message: "[AppStoreAudioCaptureStore] initializeRecording",
        info: { error: "Cannot initialize recording when the recorder is not idle" },
      });
      return;
    }

    runInAction(() => {
      if (this.status !== RecordingStatus.LOCKED) {
        this.transcriptMap.clear();
        this.transcriptSnapshots = [];
        this.audioChunks = [];
        this.capturedAudioId = uuidModule.generate();
      }
      this.status = RecordingStatus.INITIALIZING;
      this.activeNoteId = noteId;
      this.audioBuffer = [];
      this.reconnectionAttempts = 0;
      this.isReconnecting = false;
      this.isRecordingWithoutTranscription = true;
    });

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType: this.supportedMimeType });
      this.mediaRecorder.ondataavailable = this._mediaRecorderOnDataAvailable;

      // We don't need to follow Assembly AI's guidelines as transcription is not supported.
      this.audioContext = new AudioContext();

      const analyser = this.audioContext.createAnalyser();
      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.8;

      runInAction(() => {
        this.analyser = analyser;
      });

      this.audioSource = this.audioContext.createMediaStreamSource(this.stream);
      this.audioSource.connect(analyser);

      runInAction(() => {
        this.startTime = Date.now();
        this.totalPausedTime = 0;
        this.pausedTime = 0;
        this.status = RecordingStatus.INITIALIZED;
        this.error = null;
      });
    } catch (error) {
      toastModule.triggerToast({
        content: "Failed to initialize recording",
        toastId: this.capturedAudioId,
      });
      logger.error({
        message: "[AppStoreAudioCaptureStore] initializeRecordingWithoutTranscription",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
      runInAction(() => this.resetRecording());
    }
  }

  // INITIALIZED -> RECORDING
  _startRecording(): void {
    if (!this.mediaRecorder || this.status !== RecordingStatus.INITIALIZED) {
      toastModule.triggerToast({
        content: "Voice capture is not initialized",
        toastId: this.capturedAudioId,
      });
      logger.error({
        message: "[AppStoreAudioCaptureStore] startRecording",
        info: { error: "Voice capture is not initialized" },
      });
      this.resetRecording();
      return;
    }

    // Receive audio chunks every 1 second:
    this.mediaRecorder.start(1_000);
    if (this.activeNoteId) {
      this.store.memDb.audioCaptures.saveCapturedAudioId(this.activeNoteId, this.capturedAudioId);
    }

    runInAction(() => {
      this.status = RecordingStatus.RECORDING;
      this.durationInterval = setInterval(() => {
        this._updateDuration();
      }, this.DURATION_UPDATE_INTERVAL_MS);
    });

    this._startTokenMonitoring();
    this._startAudioBufferFlush();
    this._startWebSocketHealthCheck();
  }

  // IDLE (created) -> INITIALIZED (recording state restored) | IDLE (empty state)
  async initializeFromDb() {
    const state = await this.store.memDb.audioCaptures.getAudioCaptureState();
    if (!state) return;

    // If note is gone, don't lock audio capture.
    const note = await this.store.notes.getAsync(state.noteId);
    if (!note || note.isDeleted) return;

    const {
      noteId,
      capturedAudioId,
      audioBlobs: audioChunks,
      transcripts,
      transcriptSnapshots,
    } = state;
    logger.debug({
      message: "[AppStoreAudioCaptureStore] initializeFromDb",
      info: {
        noteId,
        capturedAudioId,
        audioChunks: audioChunks.length,
        transcripts: transcripts?.length,
        snapshots: transcriptSnapshots?.length,
      },
    });

    const restoredDuration = await (async (): Promise<number> => {
      if (audioChunks.length === 0) return 0;
      try {
        const blob = new Blob(audioChunks, { type: audioChunks[0].type });
        return this._getAudioDuration(blob);
      } catch (error) {
        logger.error({
          message: "[AppStoreAudioCaptureStore] initializeFromDb",
          info: { error: error instanceof Error ? error.message : String(error) },
        });
        return 0;
      }
    })();

    const audioBlob = new Blob(audioChunks, {
      type: audioChunks[0]?.type ?? "audio/webm",
    });
    const audioUrl = URL.createObjectURL(audioBlob);

    this._withAudioCaptureLock(
      async () => {
        runInAction(() => {
          this.status = RecordingStatus.LOCKED;
          this.capturedAudioId = capturedAudioId;
          this.activeNoteId = noteId;
          this.audioBlob = audioBlob;
          this.audioUrl = audioUrl;
          this.transcriptSnapshots = transcriptSnapshots || [];
          this.transcriptMap.clear();
          for (const transcript of transcripts || []) {
            this.transcriptMap.set(transcript.audio_start, transcript);
          }
          this.audioChunks = audioChunks || [];
          this.restoredDuration = restoredDuration;
        });
      },
      { showErrorToast: false }
    );
  }

  // IDLE -> RECORDING
  async initializeAndStartRecording(noteId: string): Promise<void> {
    await this._withAudioCaptureLock(async () => {
      await this._initializeRecording(noteId);
      this._startRecording();
      trackEvent(TrackedEvent.VoiceSessionStart, {
        session_id: this.sessionId,
        captured_audio_id: this.capturedAudioId,
        note_id: this.activeNoteId,
      });
    });
  }

  // RECORDING -> PAUSED
  async pauseRecording() {
    if (!this.mediaRecorder) {
      toastModule.triggerToast({
        content: "Voice capture is not initialized",
        toastId: this.capturedAudioId,
      });
      logger.error({
        message: "[AppStoreAudioCaptureStore] pauseRecording",
        info: { error: "Voice capture is not initialized" },
      });
      this.resetRecording();
      return;
    }

    if (this.status !== RecordingStatus.RECORDING) {
      toastModule.triggerToast({
        content: "Cannot pause recording when not recording",
        toastId: this.capturedAudioId,
      });
      logger.error({
        message: "[AppStoreAudioCaptureStore] pauseRecording",
        info: { error: "Cannot pause recording when not recording" },
      });
      return;
    }

    this.mediaRecorder.pause();
    await this.audioContext?.suspend();
    await this._flushAudioBuffer();

    runInAction(() => {
      this.status = RecordingStatus.PAUSED;
      this.pausedTime = Date.now();
    });

    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }

    trackEvent(TrackedEvent.VoiceSessionPause, {
      session_id: this.sessionId,
      captured_audio_id: this.capturedAudioId,
      note_id: this.activeNoteId,
      timestamp: Math.floor(Date.now() / 1000),
    });
  }

  // PAUSED -> RECORDING
  async resumeRecording() {
    if (!this.mediaRecorder) {
      toastModule.triggerToast({
        content: "Voice capture is not initialized",
        toastId: this.capturedAudioId,
      });
      logger.error({
        message: "[AppStoreAudioCaptureStore] resumeRecording",
        info: { error: "Voice capture is not initialized" },
      });
      this.resetRecording();
      return;
    }

    if (this.status !== RecordingStatus.PAUSED) {
      toastModule.triggerToast({
        content: "Cannot resume recording when not paused",
        toastId: this.capturedAudioId,
      });
      logger.error({
        message: "[AppStoreAudioCaptureStore] resumeRecording",
        info: { error: "Cannot resume recording when not paused" },
      });
      return;
    }

    this.mediaRecorder.resume();
    await this.audioContext?.resume();
    await this._flushAudioBuffer();

    runInAction(() => {
      this.status = RecordingStatus.RECORDING;
      this.totalPausedTime += Date.now() - this.pausedTime;
      this.durationInterval = setInterval(() => {
        this._updateDuration();
      }, this.DURATION_UPDATE_INTERVAL_MS);
    });

    trackEvent(TrackedEvent.VoiceSessionResume, {
      session_id: this.sessionId,
      captured_audio_id: this.capturedAudioId,
      note_id: this.activeNoteId,
      timestamp: Math.floor(Date.now() / 1000),
    });
  }

  // RECORDING -> STOPPED
  async stopRecording() {
    return new Promise<boolean>(resolve => {
      this.releaseRecordingLock?.(false);
      this.releaseRecordingLock = null;

      if (!this.mediaRecorder) {
        toastModule.triggerToast({
          content: "Voice capture is not initialized",
          toastId: this.capturedAudioId,
        });
        logger.error({
          message: "[AppStoreAudioCaptureStore] stopRecording",
          info: { error: "Voice capture is not initialized" },
        });
        this.resetRecording();
        return resolve(false);
      }

      // Check if we're in a valid state to stop recording
      if (![RecordingStatus.RECORDING, RecordingStatus.PAUSED].includes(this.status)) {
        logger.debug({
          message: "[AppStoreAudioCaptureStore] stopRecording",
          info: {
            currentStatus: this.status,
            mediaRecorderState: this.mediaRecorder.state,
            error: "Cannot stop recording in current state",
          },
        });
        return resolve(false);
      }

      // Check MediaRecorder state
      if (this.mediaRecorder.state === "inactive") {
        logger.debug({
          message: "[AppStoreAudioCaptureStore] stopRecording",
          info: {
            currentStatus: this.status,
            mediaRecorderState: this.mediaRecorder.state,
            error: "MediaRecorder is already inactive",
          },
        });
        return resolve(false);
      }

      this.mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(this.audioChunks, {
          type: this.audioChunks[0]?.type ?? "audio/webm",
        });
        const audioUrl = URL.createObjectURL(audioBlob);

        // Clean up WebSocket connection and audio resources after recording is stopped
        await this._flushAudioBuffer();
        await new Promise(resolve => setTimeout(resolve, this.POST_STOP_BUFFER_MS));
        await this._cleanupWebSocketAndAudio();

        runInAction(() => {
          this.audioBlob = audioBlob;
          this.audioUrl = audioUrl;
          this.status = RecordingStatus.STOPPED;
          if (this.durationInterval) {
            clearInterval(this.durationInterval);
            this.durationInterval = null;
          }

          this._stopTokenMonitoring();
        });

        resolve(true);
      };

      this.mediaRecorder.stop();

      trackEvent(TrackedEvent.VoiceSessionDone, {
        session_id: this.sessionId,
        captured_audio_id: this.capturedAudioId,
        note_id: this.activeNoteId,
        timestamp: Math.floor(Date.now() / 1000),
      });
    });
  }

  // STOPPED -> UPLOADING -> UPLOADED -> PROCESSING -> PROCESSED
  async processRecording() {
    const processRecordingStartTime = Date.now();

    if (
      ![RecordingStatus.STOPPED, RecordingStatus.UPLOADED, RecordingStatus.LOCKED].includes(
        this.status
      )
    ) {
      toastModule.triggerToast({
        content: "Cannot process recording when the recorder is not stopped",
        toastId: this.capturedAudioId,
      });
      logger.error({
        message: "[AppStoreAudioCaptureStore] processRecording",
        info: { error: "Cannot process recording when the recorder is not stopped" },
      });
      return;
    }

    runInAction(() => this.activeNoteObservable?.setIsProcessingSuggestion(true));

    // Try to upload recording
    // If the recording is already uploaded, skip this step
    if ([RecordingStatus.STOPPED, RecordingStatus.LOCKED].includes(this.status)) {
      runInAction(() => (this.status = RecordingStatus.UPLOADING));
      const result = await this._uploadRecording();
      if (!result.success) {
        const uploadError = result.error;
        toastModule.triggerToast({
          content: uploadError || "Failed to upload recording",
          toastId: this.capturedAudioId,
        });
        logger.error({
          message: "[AppStoreAudioCaptureStore] processRecording",
          info: { error: uploadError || "Failed to upload recording" },
        });
        runInAction(() => {
          this.status = RecordingStatus.STOPPED;
          this.error = uploadError || "Failed to upload recording";
        });
        return;
      } else {
        runInAction(() => {
          this.status = RecordingStatus.UPLOADED;
          this.error = null;
        });
      }
    }

    // Try to transform audio to note
    runInAction(() => (this.status = RecordingStatus.PROCESSING));
    const { success: transformSuccess, error: transformError } = await this._transformAudioToNote();
    if (!transformSuccess) {
      toastModule.triggerToast({
        content: transformError || "Failed to transform audio to note",
        toastId: this.capturedAudioId,
      });
      logger.error({
        message: "[AppStoreAudioCaptureStore] processRecording",
        info: { error: transformError || "Failed to transform audio to note" },
      });
      runInAction(() => {
        this.status = RecordingStatus.UPLOADED;
        this.error = transformError || "Failed to transform audio to note";
      });
      return;
    }

    const processRecordingEndTime = Date.now();
    const processRecordingDuration = Math.floor(
      (processRecordingEndTime - processRecordingStartTime) / 1000
    );
    trackEvent(TrackedEvent.VoiceSessionSuggestedEditsView, {
      session_id: this.sessionId,
      captured_audio_id: this.capturedAudioId,
      note_id: this.activeNoteId,
      elapsed_time: processRecordingDuration,
    });

    runInAction(() => this.activeNoteObservable?.setIsProcessingSuggestion(false));

    if (transformSuccess) {
      this.clearRecording();
    }
  }

  // ANY -> IDLE
  resetRecording({ preserveLockedStatus }: { preserveLockedStatus?: boolean } = {}) {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }

    this.stream = null;
    this.mediaRecorder = null;

    if (preserveLockedStatus) {
      this.status = RecordingStatus.LOCKED;
    } else {
      this.status = RecordingStatus.IDLE;
      this.restoredDuration = 0;
      this.activeNoteId = null;
      this.audioBlob = null;
      this.audioUrl = null;
      this.audioChunks = [];
      this.transcriptMap.clear();
      this.transcriptSnapshots = [];
    }

    this.duration = 0;
    this.error = null;
    this.totalPausedTime = 0;
    this.pausedTime = 0;
    this.isRefreshingToken = false;
    this.isRecordingWithoutTranscription = false;

    // Reset buffering and reconnection state
    this.audioBuffer = [];
    this.isReconnecting = false;
    this.reconnectionAttempts = 0;
    this.isWebSocketHealthy = false;
    this.lastWebSocketActivity = 0;

    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }

    if (this.reconnectionTimeout) {
      clearTimeout(this.reconnectionTimeout);
      this.reconnectionTimeout = null;
    }

    if (this.releaseRecordingLock) {
      this.releaseRecordingLock(false);
      this.releaseRecordingLock = null;
    }

    this._stopTokenMonitoring();
    this._stopAudioBufferFlush();
    this._stopWebSocketHealthCheck();

    this.activeNoteObservable?.setIsProcessingSuggestion(false);
  }

  clearRecording() {
    // We could target a single noteId here but we risk leaving ancient recordings behind,
    // taking considerable space.
    this.store.memDb.audioCaptures.clear();
    this.resetRecording();
  }

  async clearAudioCapturesByNoteId(noteId: string) {
    await this.store.memDb.audioCaptures.clearAudioCapturesByNoteId(noteId);
    if (this.activeNoteId === noteId) this.resetRecording();
  }

  /**
   * WEBSOCKET MANAGEMENT
   */

  async _createWebSocketConnection(): Promise<void> {
    if (!this.assemblyAiToken) {
      throw new Error("No valid token available for WebSocket connection");
    }

    const params = new URLSearchParams({
      sample_rate: this.SAMPLE_RATE.toString(),
      token: this.assemblyAiToken,
    });

    return new Promise((resolve, reject) => {
      try {
        this.wsConnection = new WebSocket(`${this.ASSEMBLY_AI_WS_URL}?${params.toString()}`);

        const connectionTimeout = setTimeout(() => {
          if (this.wsConnection?.readyState !== WebSocket.OPEN) {
            this.wsConnection?.close();
            reject(new Error("WebSocket connection timeout"));
          }
        }, this.WEBSOCKET_CONNECTION_TIMEOUT_MS);

        this.wsConnection.onopen = () => {
          clearTimeout(connectionTimeout);
          logger.debug({
            message: "[AppStoreAudioCaptureStore] WebSocket Connected",
          });

          runInAction(() => {
            this.isWebSocketHealthy = true;
            this.lastWebSocketActivity = Date.now();
            this.reconnectionAttempts = 0;
            this.isReconnecting = false;
          });

          // Flush any buffered audio data after successful connection
          this._flushAudioBuffer();

          resolve();
        };

        this.wsConnection.onmessage = event => {
          runInAction(() => (this.lastWebSocketActivity = Date.now()));
          this._handleWebSocketMessage(event);
        };

        this.wsConnection.onerror = error => {
          clearTimeout(connectionTimeout);
          logger.error({
            message: "[AppStoreAudioCaptureStore] WebSocket Error",
            info: { error: error instanceof Error ? error.message : String(error) },
          });
          runInAction(() => (this.isWebSocketHealthy = false));
          reject(new Error("Failed to connect to transcription service"));
        };

        this.wsConnection.onclose = event => {
          clearTimeout(connectionTimeout);
          logger.debug({
            message: "[AppStoreAudioCaptureStore] WebSocket Closed",
            info: { code: event.code, reason: event.reason },
          });
          runInAction(() => (this.isWebSocketHealthy = false));
          if (
            this.status === RecordingStatus.RECORDING &&
            !this.isReconnecting &&
            !this.isRefreshingToken
          ) {
            this._handleWebSocketDisconnection();
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  async _initializeWebSocket(): Promise<void> {
    try {
      if (this._isTokenExpiredOrExpiringSoon()) {
        await this._getAssemblyAiToken();
      }
      await this._createWebSocketConnection();
    } catch (error) {
      logger.error({
        message: "[AppStoreAudioCaptureStore] Failed to initialize WebSocket, retrying later...",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
      this._attemptWebSocketReconnectionAfterDelay();
    }
  }

  _handleWebSocketMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data) as AssemblyAIMessage;
      if (this.restoredDuration > 0) {
        data.audio_start += this.restoredDuration * 1000;
      }
      if (data.message_type === "FinalTranscript") {
        this.transcriptMap.set(data.audio_start, data);
        if (this.activeNoteId) {
          this.store.memDb.audioCaptures.appendTranscript(this.activeNoteId, data);
        }
      } else if (data.message_type === "PartialTranscript") {
        this.transcriptMap.set(data.audio_start, data);
        if (this.activeNoteId) {
          this.store.memDb.audioCaptures.appendTranscript(this.activeNoteId, data);
        }
      }
    } catch (error) {
      logger.error({
        message: "[AppStoreAudioCaptureStore] WebSocket Message Error",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
    }
  }

  _sendAudioToWebSocket(audioData: ArrayBuffer): void {
    // Always buffer audio first - this ensures no audio is ever lost
    this._bufferAudioData(audioData);

    // Then try to flush the buffer if WebSocket is available
    if (this.wsConnection?.readyState === WebSocket.OPEN && this.isWebSocketHealthy) {
      this._flushAudioBuffer();
    }
  }

  async _cleanupWebSocketAndAudio(): Promise<void> {
    // Send terminate message to AssemblyAI
    if (this.wsConnection?.readyState === WebSocket.OPEN) {
      this.wsConnection.send(JSON.stringify({ terminate_session: true }));
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    // Close websocket, audioWorkletNode, audioSource, and audioContext
    runInAction(() => {
      if (this.wsConnection) {
        this.wsConnection.close();
        this.wsConnection = null;
      }
      if (this.audioWorkletNode) {
        this.audioWorkletNode.disconnect();
        this.audioWorkletNode = null;
      }
      if (this.audioSource) {
        this.audioSource.disconnect();
        this.audioSource = null;
      }
      if (this.analyser) {
        this.analyser.disconnect();
        this.analyser = null;
      }
    });

    if (this.audioContext) {
      await this.audioContext.close();
      runInAction(() => (this.audioContext = null));
    }
  }

  /**
   * TOKEN MANAGEMENT
   */

  async _getAssemblyAiToken(): Promise<void> {
    try {
      const response = await api.post("/v2/voice/start_transcription");
      if (!response.data?.token) throw new Error("Failed to get AssemblyAI token");
      runInAction(() => {
        this.assemblyAiToken = response.data.token;
        this.assemblyAiTokenExpiresAt = response.data.expires_at
          ? new Date(response.data.expires_at).getTime()
          : null;
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to get AssemblyAI token";
      logger.error({
        message: "[AppStoreAudioCaptureStore] Get AssemblyAI Token Error",
        info: { error: errorMessage },
      });
      throw new Error(errorMessage);
    }
  }

  _isTokenExpiredOrExpiringSoon(): boolean {
    if (!this.assemblyAiToken || !this.assemblyAiTokenExpiresAt) {
      return true;
    }

    const now = Date.now();
    const expiresAt = this.assemblyAiTokenExpiresAt;

    return now >= expiresAt - this.TOKEN_EXPIRATION_BUFFER_MS;
  }

  async _refreshTokenAndReconnectWebSocket(): Promise<void> {
    if (!this._isTokenExpiredOrExpiringSoon() || this.isRefreshingToken) {
      return;
    }

    runInAction(() => (this.isRefreshingToken = true));

    logger.debug({
      message:
        "[AppStoreAudioCaptureStore] Refreshing token and reconnecting WebSocket during recording",
      info: {
        currentTime: Date.now(),
        expiresAt: this.assemblyAiTokenExpiresAt,
        wsState: this.wsConnection?.readyState ?? null,
      },
    });

    try {
      await this._getAssemblyAiToken();

      // Only reconnect WebSocket if we're still recording
      if (this.status === RecordingStatus.RECORDING) {
        if (this.wsConnection) {
          this._createTranscriptSnapshot();
          this.wsConnection.close();
          this.wsConnection = null;
        }
        await this._createWebSocketConnection();
      }
    } catch (error) {
      logger.error({
        message: "[AppStoreAudioCaptureStore] Failed to refresh token during recording",
        info: { error: error instanceof Error ? error.message : String(error) },
      });

      toastModule.triggerToast({
        content: "Voice transcription may be interrupted due to connection issues",
        toastId: this.capturedAudioId,
      });
    } finally {
      runInAction(() => (this.isRefreshingToken = false));
    }
  }

  _startTokenMonitoring(): void {
    if (this.tokenCheckInterval) {
      this._stopTokenMonitoring();
    }

    this.tokenCheckInterval = setInterval(async () => {
      try {
        if (this.status === RecordingStatus.RECORDING) {
          await this._refreshTokenAndReconnectWebSocket();
        }
      } catch (error) {
        logger.error({
          message: "[AppStoreAudioCaptureStore] Error in token monitoring interval",
          info: { error: error instanceof Error ? error.message : String(error) },
        });
      }
    }, this.TOKEN_CHECK_INTERVAL_MS);
  }

  _stopTokenMonitoring(): void {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
  }

  /**
   * BUFFERING AND RECONNECTION HELPERS
   */

  _bufferAudioData(audioData: ArrayBuffer): void {
    if (this.audioBuffer.length >= this.MAX_AUDIO_BUFFER_SIZE) {
      this.audioBuffer.shift();
    }
    this.audioBuffer.push({ data: audioData, timestamp: Date.now() });
  }

  _flushAudioBuffer(): void {
    if (
      this.audioBuffer.length === 0 ||
      !this.wsConnection ||
      this.wsConnection.readyState !== WebSocket.OPEN
    ) {
      return;
    }

    const bufferedItems = [...this.audioBuffer];
    runInAction(() => (this.audioBuffer = []));

    const wsConnection = this.wsConnection;
    bufferedItems.forEach(item => {
      if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
        wsConnection.send(item.data);
      }
    });
  }

  _startAudioBufferFlush(): void {
    if (this.audioBufferFlushInterval) {
      this._stopAudioBufferFlush();
    }

    this.audioBufferFlushInterval = setInterval(() => {
      this._flushAudioBuffer();
    }, this.AUDIO_BUFFER_FLUSH_INTERVAL_MS);
  }

  _stopAudioBufferFlush(): void {
    if (this.audioBufferFlushInterval) {
      clearInterval(this.audioBufferFlushInterval);
      this.audioBufferFlushInterval = null;
    }
  }

  _handleWebSocketDisconnection(): void {
    if (this.isReconnecting || this.isRefreshingToken || this.isRecordingWithoutTranscription)
      return;

    logger.warn({
      message: "[AppStoreAudioCaptureStore] WebSocket disconnected, attempting to reconnect",
      info: { attempts: this.reconnectionAttempts },
    });

    toastModule.triggerToast({
      content: "Connection to transcription service lost. Attempting to reconnect...",
      toastId: this.capturedAudioId,
    });

    this._createTranscriptSnapshot();
    this._attemptWebSocketReconnection();
  }

  _attemptWebSocketReconnectionAfterDelay = debounce(
    () => this._attemptWebSocketReconnection(),
    // 1 second delay between reconnection attempts
    1000,
    { trailing: true, leading: false }
  );

  async _attemptWebSocketReconnection(): Promise<void> {
    this._attemptWebSocketReconnectionAfterDelay.cancel();

    const currentStateExpectsWebSocket = () => {
      return [
        RecordingStatus.INITIALIZING,
        RecordingStatus.INITIALIZED,
        RecordingStatus.RECORDING,
        RecordingStatus.PAUSED,
      ].includes(this.status);
    };

    if (!currentStateExpectsWebSocket()) return;

    if (this.reconnectionAttempts >= this.MAX_RECONNECTION_ATTEMPTS) {
      logger.error({
        message: "[AppStoreAudioCaptureStore] Max reconnection attempts reached",
        info: { attempts: this.reconnectionAttempts },
      });
      toastModule.triggerToast({
        content:
          "Unable to connect to transcription service. Your audio is still being recorded, but real-time transcription may be unavailable until the connection is restored.",
        toastId: this.capturedAudioId,
      });
      runInAction(() => {
        this.isReconnecting = false;
        this.audioBuffer = [];
      });
      return;
    }

    runInAction(() => {
      this.isReconnecting = true;
      this.reconnectionAttempts++;
    });

    try {
      await new Promise(resolve => {
        this.reconnectionTimeout = setTimeout(resolve, this.RECONNECTION_DELAY_MS);
      });

      if (this.wsConnection) {
        this.wsConnection.close();
        this.wsConnection = null;
      }

      if (!currentStateExpectsWebSocket()) return;

      if (this._isTokenExpiredOrExpiringSoon()) {
        await this._getAssemblyAiToken();
      }

      await this._createWebSocketConnection();

      logger.debug({
        message: "[AppStoreAudioCaptureStore] WebSocket reconnection successful",
        info: { attempts: this.reconnectionAttempts },
      });
    } catch (error) {
      logger.error({
        message: "[AppStoreAudioCaptureStore] WebSocket reconnection failed",
        info: {
          error: error instanceof Error ? error.message : String(error),
          attempts: this.reconnectionAttempts,
        },
      });

      if (this.reconnectionAttempts <= this.MAX_RECONNECTION_ATTEMPTS) {
        runInAction(() => (this.isReconnecting = false));
        this._attemptWebSocketReconnectionAfterDelay();
      }
    }
  }

  _createTranscriptSnapshot(): void {
    const sortedMessages = Array.from(this.transcriptMap.values()).sort(
      (a, b) => a.audio_start - b.audio_start
    );
    const lastMessage = sortedMessages[sortedMessages.length - 1];

    const snapshot: TranscriptSnapshot = {
      // Remove proxies so we can serialize the snapshot.
      transcriptMap: new Map(toJS(this.transcriptMap)),
      lastAudioEnd: lastMessage?.audio_end || 0,
      snapshotTime: Date.now(),
    };

    runInAction(() => {
      this.transcriptSnapshots.push(snapshot);
      this.transcriptMap.clear();
    });

    if (this.activeNoteId) {
      this.store.memDb.audioCaptures.appendTranscriptSnapshot(this.activeNoteId, snapshot);
    }

    logger.debug({
      message: "[AppStoreAudioCaptureStore] Created transcript snapshot",
      info: {
        transcriptSize:
          this.transcriptSnapshots[this.transcriptSnapshots.length - 1].transcriptMap.size,
        lastAudioEnd: this.transcriptSnapshots[this.transcriptSnapshots.length - 1].lastAudioEnd,
        totalMessages: sortedMessages.length,
      },
    });
  }

  _startWebSocketHealthCheck(): void {
    if (this.webSocketHealthCheckInterval) {
      this._stopWebSocketHealthCheck();
    }

    this.webSocketHealthCheckInterval = setInterval(() => {
      this._checkWebSocketHealth();
    }, this.TOKEN_CHECK_INTERVAL_MS);
  }

  _stopWebSocketHealthCheck(): void {
    if (this.webSocketHealthCheckInterval) {
      clearInterval(this.webSocketHealthCheckInterval);
      this.webSocketHealthCheckInterval = null;
    }
  }

  _checkWebSocketHealth(): void {
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastWebSocketActivity;

    // Consider connection unhealthy if no activity for more than 30 seconds during recording
    const isHealthy =
      this.wsConnection?.readyState === WebSocket.OPEN &&
      (this.status !== RecordingStatus.RECORDING || timeSinceLastActivity < 30000);

    if (!isHealthy && this.isWebSocketHealthy) {
      logger.warn({
        message: "[AppStoreAudioCaptureStore] WebSocket health check failed",
        info: {
          readyState: this.wsConnection?.readyState ?? null,
          timeSinceLastActivity,
          status: this.status,
        },
      });

      runInAction(() => (this.isWebSocketHealthy = false));

      if (
        this.status === RecordingStatus.RECORDING &&
        !this.isReconnecting &&
        !this.isRefreshingToken
      ) {
        this._handleWebSocketDisconnection();
      }
    } else if (isHealthy && !this.isWebSocketHealthy) {
      runInAction(() => (this.isWebSocketHealthy = true));
    }
  }
}
